import React from 'react';
import { PreparationFlowVisualization } from './PreparationFlowVisualization';
import { PositioningSections } from './PositioningSections';
import { UploadCloudIcon } from './icons/UploadCloudIcon';
import {
  HERO_HEADLINE_ACCENT,
  HERO_REVIEW_BANNER,
  HERO_SECURITY_NOTE,
  HERO_SUPPORT,
  PREPARATION_FLOW,
  PRODUCT_POSITIONING_PHRASE,
} from '../config/productCopy';

interface HomeLandingProps {
  onPrepareDocument: () => void;
  onLogin: () => void;
}

export const HomeLanding: React.FC<HomeLandingProps> = ({ onPrepareDocument, onLogin }) => {
  const scrollToHowItWorks = () => {
    document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="w-full">
      {/* Soft brand wash — light only. Overflow is limited to the decorative layer so the graphic is never clipped. */}
      <div className="relative rounded-3xl border border-slate-200/80 bg-gradient-to-br from-white via-slate-50 to-indigo-50/40 shadow-sm">
        <div
          className="pointer-events-none absolute inset-0 overflow-hidden rounded-3xl opacity-40"
          style={{
            backgroundImage:
              'radial-gradient(ellipse 80% 50% at 100% 0%, rgba(99,102,241,0.12), transparent 55%), radial-gradient(ellipse 60% 40% at 0% 100%, rgba(16,185,129,0.1), transparent 50%)',
          }}
          aria-hidden
        />

        <div className="relative grid grid-cols-1 gap-8 p-6 sm:gap-10 sm:p-8 lg:p-10 xl:grid-cols-2 xl:gap-10 2xl:gap-12">
          {/* Left copy */}
          <div className="flex max-w-xl flex-col justify-center text-left min-w-0">
            <p className="text-xs sm:text-sm font-semibold tracking-[0.12em] uppercase text-indigo-700 mb-3">
              {PRODUCT_POSITIONING_PHRASE}
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-[2.6rem] font-bold tracking-tight text-slate-900 leading-[1.15]">
              Prepare Educational Documents for AI —{' '}
              <span className="text-indigo-700">{HERO_HEADLINE_ACCENT}</span>
            </h2>
            <p className="mt-4 text-base sm:text-lg text-slate-600 leading-relaxed">{HERO_SUPPORT}</p>

            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={onPrepareDocument}
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-emerald-600 text-white font-semibold shadow-lg shadow-emerald-600/20 hover:bg-emerald-700 transition-colors"
              >
                <UploadCloudIcon className="w-5 h-5" />
                Prepare a Document
              </button>
              <button
                type="button"
                onClick={scrollToHowItWorks}
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl border border-slate-300 bg-white text-slate-800 font-semibold hover:bg-slate-50 transition-colors"
              >
                <svg className="w-5 h-5 text-indigo-600" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path d="M8 5v14l11-7L8 5z" />
                </svg>
                See How It Works
              </button>
            </div>

            <p className="mt-4 flex items-start gap-2 text-sm text-slate-500">
              <svg className="w-4 h-4 text-indigo-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <span>{HERO_SECURITY_NOTE}</span>
            </p>

            <p className="mt-6 text-sm text-slate-500">
              Already have an account?{' '}
              <button type="button" onClick={onLogin} className="font-semibold text-emerald-700 hover:underline">
                Sign in
              </button>
            </p>
          </div>

          {/* Right visualization */}
          <div className="min-w-0">
            <PreparationFlowVisualization />
            <div className="mt-4 rounded-xl border border-indigo-100 bg-indigo-50/80 px-4 py-3 flex items-start gap-2.5">
              <svg className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              <p className="text-sm text-indigo-900">{HERO_REVIEW_BANNER}</p>
            </div>
          </div>
        </div>
      </div>

      {/* How it works */}
      <section id="how-it-works" className="mt-14 scroll-mt-24">
        <h2 className="text-2xl sm:text-3xl font-bold text-center text-slate-900">How Redact AI Works</h2>
        <p className="mt-2 text-center text-slate-600 max-w-2xl mx-auto">
          A simple 3-step process to prepare your educational documents for AI.
        </p>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-4 relative">
          {PREPARATION_FLOW.map((item, index) => (
            <div key={item.step} className="relative">
              {index < PREPARATION_FLOW.length - 1 && (
                <div
                  className="hidden md:block absolute top-10 -right-2 w-4 h-4 text-indigo-300 z-10"
                  aria-hidden
                >
                  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                    <path d="M13.172 12l-4.95-4.95 1.414-1.414L16 12l-6.364 6.364-1.414-1.414z" />
                  </svg>
                </div>
              )}
              <div className="h-full rounded-2xl border border-slate-200 bg-white p-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-center gap-3 mb-3">
                  <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-indigo-100 text-indigo-800 text-sm font-bold">
                    {item.step}
                  </span>
                  <h3 className="text-lg font-semibold text-slate-900">{item.title}</h3>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed">{item.description}</p>
              </div>
            </div>
          ))}
        </div>
        <p className="mt-5 text-center text-sm text-slate-500">
          Educational Document → Redact AI → Prepared Document
        </p>
      </section>

      {/* Trust strip */}
      <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-4 rounded-2xl border border-slate-200 bg-white px-5 py-6 shadow-sm">
        <div className="flex items-start gap-3">
          <svg className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
          <div>
            <p className="text-sm font-semibold text-slate-800">You stay in control</p>
            <p className="text-xs text-slate-500 mt-0.5">Prepare a working copy — your original file stays with you.</p>
          </div>
        </div>
        <div className="flex items-start gap-3">
          <svg className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
          <div>
            <p className="text-sm font-semibold text-slate-800">Not stored by Redact AI</p>
            <p className="text-xs text-slate-500 mt-0.5">Document contents are processed to prepare your file, then discarded — not saved in our app database.</p>
          </div>
        </div>
        <div className="flex items-start gap-3">
          <svg className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
          <div>
            <p className="text-sm font-semibold text-slate-800">Built for educators</p>
            <p className="text-xs text-slate-500 mt-0.5">Focused on student privacy.</p>
          </div>
        </div>
      </div>

      <PositioningSections />
    </div>
  );
};

export default HomeLanding;
